import { useMemo, useRef, useState, type ChangeEvent, type FormEvent, type ReactNode } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { requireAuth } from '../features/auth/guards.js';
import { useForm } from '@tanstack/react-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { z } from 'zod';
import { PageHeader } from '@/components/account/PageHeader.js';
import { StatusPill } from '@/components/account/StatusPill.js';
import { TransactionRow } from '@/components/account/TransactionRow.js';
import { BankLogo } from '@/components/account/BankLogo.js';
import { Card } from '@/components/ui/card.js';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { Badge } from '@/components/ui/badge.js';
import { Icon } from '@/components/ui/icon.js';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs.js';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog.js';
import { formatMoney, tryToMinorUnits, minorToPlain, currencyGlyph, decimalPlacesFor, normalizeAmountInput } from '@/lib/money/index.js';
import { ArrowDownToLine, Paperclip, X } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import {
  fetchDeposits,
  createDeposit,
  cancelDeposit,
  isCancellableDeposit,
  isResumableDeposit,
  uploadDepositSlip,
  useBankCurrency,
  useBankMasters,
  useHouseBanks,
  useTransferMinimums,
  FINANCE_QUERY_KEYS,
  type BankMaster,
  type Deposit,
  type HouseBank,
} from '@/features/finance/api.js';
import { bankLabel } from '@/features/finance/bank-label.js';
import { depositSourceBanks } from '@/features/finance/bank-eligibility.js';
import {
  banksForMode,
  depositModeBlocker,
  enabledDepositModes,
  type DepositMode,
  type DepositModeBlocker,
} from '@/features/finance/deposit-modes.js';
import { useFinanceConfig, FINANCE_CONFIG_DEFAULTS } from '@/features/finance/config.js';
import { fetchBankAccounts, MEMBER_QUERY_KEYS, type BankAccount } from '@/features/member/api.js';
import { WALLET_QUERY_KEYS } from '@/features/wallet/api.js';
import { useAuthStore } from '@/stores/auth.js';
import { bigIntish, fetchMyClaims, PROMO_QUERY_KEYS } from '@/features/promo/api.js';
import { findDepositReservation, indexBonusByDeposit } from '@/features/promo/deposit-claims.js';
import { mapApiError, ApiError } from '@/lib/api/error.js';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '@/lib/datetime/format.js';
import { useDisplayMoney, useFormatMoney, useMoneyCurrency } from '@/components/money/MoneyProvider.js';
import { moneyView, parseRateScaled, rateLabel, type MoneyView } from '@/lib/money/display-rate.js';

export const Route = createFileRoute('/_player/wallet/deposit')({
  beforeLoad: requireAuth,
  component: DepositScreen,
});

// The accepted decimals follow the CURRENCY, not a hardcoded 2. A JPY brand
// has no minor unit at all, so `500.50` is not an amount a player can deposit
// (WEB-02).
//
// `minMinor` is the agent's configured deposit floor, straight from the resolver
// svc-finance refuses FIN-306 against. `null` is a real answer, not a missing
// one: an agent with no floor anywhere accepts any amount, and a client-side
// default here would be a second minimum no operator can see or change.
export type DepositMessages = {
  invalidAmount: string;
  /** The 0dp wording — "a whole amount", where "500.00" is not an amount at all. */
  invalidAmountWhole: string;
  minAmount: string;
  bankRequired: string;
};

/** i18next's `t`, narrowed to what the copy below actually asks of it. */
type Translate = (key: string, values?: Record<string, unknown>) => string;

/**
 * The form's refusal copy, with the agent's configured floor rendered into the
 * minimum sentence.
 *
 * Every sentence here used to be an English literal inlined in the schema, so a
 * Thai player who mistyped an amount was answered in English by a screen whose
 * every other word was Thai — and the catalogs already carried the translations,
 * unused (#235). Split out of the component for the same reason as the withdraw
 * twin: the interpolation is checkable without a DOM.
 */
export function depositMessages(
  t: Translate,
  currency: string,
  minMinor: bigint | null,
): DepositMessages {
  return {
    invalidAmount: t('form.invalidAmount'),
    invalidAmountWhole: t('form.invalidAmountWhole'),
    minAmount: t('form.minAmount', {
      amount: minMinor === null ? '' : formatMoney(minMinor, currency),
    }),
    bankRequired: t('form.bankRequired'),
  };
}

export function buildDepositSchema(
  currency: string,
  minMinor: bigint | null,
  messages: DepositMessages,
) {
  const dp = decimalPlacesFor(currency);
  const pattern = dp === 0 ? /^\d+$/ : new RegExp(`^\\d+(\\.\\d{1,${dp}})?$`);
  const invalid = dp === 0 ? messages.invalidAmountWhole : messages.invalidAmount;
  return z.object({
    // superRefine rather than a chained `.refine()`: a failed `.regex()` marks
    // the parse DIRTY rather than ABORTED, so a chained check still runs on
    // input the pattern already rejected. Bailing with z.NEVER removes that.
    amount: z.string().superRefine((raw, ctx) => {
      // A pasted figure brings its surrounding spaces along far more often than
      // anyone types them, and "500 " refused as malformed is indistinguishable
      // on screen from 500 refused — which is how #235 read as a broken minimum.
      const v = normalizeAmountInput(raw);
      if (!pattern.test(v)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: invalid });
        return z.NEVER;
      }
      if (minMinor === null) return;
      const parsed = tryToMinorUnits(v, currency);
      if (!parsed.ok) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: invalid });
        return z.NEVER;
      }
      if (parsed.minor < minMinor) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: messages.minAmount });
      }
    }),
    bankAccountId: z.string().uuid(messages.bankRequired),
  });
}

type DepositForm = { amount: string; bankAccountId: string };

/**
 * Blank amount on purpose — the withdraw twin's own reasoning, applied here. A
 * pre-filled figure reads as a decision the player already made, and the one
 * that shipped (500) sits UNDER any floor an operator sets above it: the screen
 * would state a THB 1,000 minimum and hand the player 500 to submit. The floor
 * becomes the placeholder instead, so the box suggests a figure that works.
 */
export const DEPOSIT_FORM_DEFAULTS: DepositForm = {
  amount: '',
  bankAccountId: '',
};

/**
 * `deposits.status` is one of pending | verifying | approved | rejected |
 * cancelled. Every settled value needs a branch of its own, because whatever
 * reaches the fallthrough tells the player their deposit is still being
 * processed: true of 'verifying', where an operator is mid-check, and false of
 * 'cancelled', which nobody will ever act on.
 *
 * The return values are PILL names, not wire values. 'credited', 'matched' and
 * 'auto_failed' are still accepted as inputs: the parameter is a bare `string`
 * because it is wire data, and those three came from a stale contracts union
 * (deleted in #344) that an older caller may yet echo back.
 */
export function depositStatusToDisplay(
  status: string,
): 'approved' | 'pending' | 'rejected' | 'cancelled' {
  if (status === 'approved' || status === 'credited' || status === 'matched') return 'approved';
  if (status === 'rejected' || status === 'auto_failed') return 'rejected';
  if (status === 'cancelled') return 'cancelled';
  return 'pending';
}

/**
 * What a deposit is typed in, given the house accounts it will be paid into:
 * an account receiving in another currency at a stated rate makes the player
 * type in THAT currency, converted to ledger minor units at the account's rate
 * (truncated) for `POST /deposits`. Otherwise it is whatever the site shows
 * (`site`, from `useDisplayMoney`), converted at the site's display rate — the
 * ledger currency as-is on a site with none.
 *
 * svc-finance already narrows the accounts to the player's currency, so the
 * first converting account speaks for the list.
 */
export type DepositInputView = MoneyView & {
  /** The house account svc-finance prices a converted amount at; null when no account does. */
  transferAccountId: string | null;
};

export function depositInputView(
  banks: Pick<HouseBank, 'id' | 'currency' | 'convertRate'>[],
  site: MoneyView,
): DepositInputView {
  const foreign = banks.find(
    (b) =>
      b.currency != null &&
      b.currency.toUpperCase() !== site.ledgerCurrency &&
      parseRateScaled(b.convertRate) !== null,
  );
  return foreign
    ? { ...moneyView(site.ledgerCurrency, foreign.currency, foreign.convertRate), transferAccountId: foreign.id }
    : { ...site, transferAccountId: null };
}

/**
 * The amount fields of `POST /deposits` for a figure typed in `view`'s currency.
 * A converted figure goes as `originalAmount` + `transferAccountId` so svc-finance
 * prices it at the account's own rate (#639); `amount` rides along as the estimate.
 */
export function depositAmountFields(
  view: DepositInputView,
  typedMinor: bigint,
): { amount: number; originalAmount?: number; transferAccountId?: string } {
  const amount = Number(view.toLedger(typedMinor));
  return view.transferAccountId
    ? { amount, originalAmount: Number(typedMinor), transferAccountId: view.transferAccountId }
    : { amount };
}

/** "You will be credited about ฿X at 700 LAK = 1 THB" under a converting amount box. */
function CreditEstimate({ view, typed }: { view: MoneyView; typed: string }) {
  const { t } = useTranslation('deposit');
  if (view.rate === null) return null;
  const parsed = tryToMinorUnits(normalizeAmountInput(typed || '0'), view.currency);
  const rate = rateLabel(view.rate, view.currency, view.ledgerCurrency);
  const credited = parsed.ok && parsed.minor > 0n ? view.toLedger(parsed.minor) : null;
  return (
    <p aria-live="polite" className="text-[12px] text-[var(--text-secondary)] bf-tnum">
      {credited === null
        ? t('conversion.rate', { rate })
        : t('conversion.credited', { amount: formatMoney(credited, view.ledgerCurrency), rate })}
    </p>
  );
}

/**
 * Withdraw a deposit request the player filed and no operator has settled yet.
 *
 * Behind a confirmation because the screen cannot see what the player did with
 * their bank: the request is only a declaration that money is on its way, so
 * someone who has already transferred and then cancels leaves a payment with
 * nothing to match it to. The dialog says so rather than assuming a stray tap
 * meant it.
 */
function CancelDepositButton({ depositId }: { depositId: string }) {
  const { t } = useTranslation('deposit');
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  const cancelMutation = useMutation({
    mutationFn: () => cancelDeposit(depositId),
    onSuccess: () => {
      setOpen(false);
      void qc.invalidateQueries({ queryKey: FINANCE_QUERY_KEYS.deposits() });
      toast.success(t('recent.cancelSuccess'));
    },
    onError: (err) => {
      // Closed either way: every refusal here is terminal for this attempt —
      // an operator got there first, or the row is already gone — and leaving
      // the dialog open invites a second press that can only fail again. The
      // list refetch is what shows the player the status that overtook them.
      setOpen(false);
      void qc.invalidateQueries({ queryKey: FINANCE_QUERY_KEYS.deposits() });
      toast.error(mapApiError(err, t));
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button type="button" variant="ghost" size="sm">
          {t('recent.cancel')}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('recent.cancelTitle')}</DialogTitle>
          <DialogDescription>{t('recent.cancelBody')}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="secondary" disabled={cancelMutation.isPending}>
              {t('recent.cancelDismiss')}
            </Button>
          </DialogClose>
          <Button
            type="button"
            variant="danger"
            disabled={cancelMutation.isPending}
            onClick={() => cancelMutation.mutate()}
          >
            {t('recent.cancelConfirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The account number is the one thing a player has to retype by hand otherwise.
 *
 * `navigator.clipboard` is absent outside a secure context, so the control is
 * not rendered at all there — offering a button that can only fail is worse than
 * offering none, and the number beside it is still selectable by hand. When the
 * API exists but the write is refused (a denied permission, a background tab)
 * the player is told, rather than left pressing a button that does nothing.
 */
function CopyAccountNumber({ value, label }: { value: string; label: string }) {
  const { t } = useTranslation('deposit');
  if (typeof navigator === 'undefined' || !navigator.clipboard) return null;
  function copy() {
    void navigator.clipboard
      .writeText(value)
      .then(() => toast.success(t('operatorBank.copied')))
      .catch(() => toast.error(t('operatorBank.copyFailed')));
  }
  return (
    <button
      type="button"
      onClick={copy}
      aria-label={label}
      className="shrink-0 h-9 w-9 grid place-items-center rounded-lg border border-[var(--border)] text-[var(--text-secondary)] hover:border-[var(--primary)] hover:text-[var(--primary)] transition-colors"
    >
      <Icon name="copy" size={16} />
    </button>
  );
}

/**
 * The house accounts a player may transfer INTO, which the deposit page had
 * never shown: the operator could configure a QR payload and a per-account
 * floor and neither ever reached a screen, so the player was asked to transfer
 * to an account they had no way of reading.
 *
 * Only deposit-capable rails are offered. A withdraw-only account is a payout
 * source, and printing its number here invites money into an account nobody
 * reconciles against deposits.
 *
 * The list is already narrowed server-side — by the agent, by
 * `visibleToMembers`, and by the member-group rule — so nothing here filters on
 * visibility. What arrives is what this member is allowed to see.
 */
function TransferTargets({
  banks,
  bankByCode,
  ledgerCurrency,
  enforcedFloorMinor,
  isPending,
  isError,
  title,
  hint,
  emptyText,
}: {
  banks: HouseBank[];
  bankByCode: Map<string, BankMaster>;
  ledgerCurrency: string;
  /** The floor svc-finance actually refuses FIN-306 against, or null. */
  enforcedFloorMinor: bigint | null;
  isPending: boolean;
  isError: boolean;
  title?: string;
  hint?: ReactNode;
  /**
   * What to say when the list is empty. The caller decides, because it knows
   * WHY: an agent with no house account at all is a different sentence from one
   * whose accounts are all on other deposit flows, and telling a player to
   * contact support about the second would send them after the wrong thing.
   */
  emptyText?: string;
}) {
  const { t, i18n } = useTranslation('deposit');

  return (
    <Card className="p-5 md:p-6 flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h3 className="text-[15px] font-bold">{title ?? t('transferTo.title')}</h3>
        {hint}
      </div>
      {isPending ? (
        <div className="flex flex-col gap-3">
          {[1, 2].map((i) => (
            <div key={i} className="h-[88px] rounded-xl bg-[var(--bg-elevated)]/50 animate-pulse" />
          ))}
        </div>
      ) : isError ? (
        <p className="text-[12px] text-[var(--text-muted)]">{t('operatorBank.error')}</p>
      ) : banks.length === 0 ? (
        <p className="text-[12px] text-[var(--text-muted)]">{emptyText ?? t('operatorBank.empty')}</p>
      ) : (
        <div className="flex flex-col gap-3">
          {banks.map((b) => {
            // A PromptPay rail is not a bank: its identifier is a phone number
            // or a national ID, and the reserved code behind it has no logo and
            // no name worth printing. What the type does NOT decide is the QR.
            const isPromptPay = b.accountType === 'promptpay';
            // The operator's switch, per account, on either kind. Absent reads
            // as ON: this SPA deploys separately from svc-finance and can be the
            // newer of the two, and a strict read would blank the QR on every
            // account for that whole window — which is the same thing the
            // column's `DEFAULT true` refuses to do.
            const showQr = b.showQr !== false;
            const accountMoney = moneyView(ledgerCurrency, b.currency, b.convertRate);
            return (
            <div
              key={b.id}
              className="flex flex-col sm:flex-row sm:items-center gap-4 p-4 rounded-xl border border-[var(--border)]"
            >
              {isPromptPay ? (
                <span
                  className="grid place-items-center shrink-0 rounded-lg border border-[var(--border)] bg-[var(--bg-elevated)] text-[var(--text-secondary)]"
                  style={{ width: 44, height: 44 }}
                  aria-hidden="true"
                >
                  <Icon name="qr" size={24} />
                </span>
              ) : (
                <BankLogo code={b.bankCode} size={44} />
              )}
              <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                <span className="text-[13px] font-semibold text-[var(--text-primary)] truncate">
                  {isPromptPay
                    ? t('operatorBank.promptpay')
                    : bankLabel(
                        bankByCode.get(b.bankCode) ?? { name: b.bankCode, country: null },
                        i18n.language,
                      )}
                </span>
                <div className="flex items-center gap-2">
                  <div className="font-mono text-[17px] font-bold tracking-wide text-[var(--text-primary)] bf-tnum truncate">
                    {b.accountNumber}
                  </div>
                  <CopyAccountNumber
                    value={b.accountNumber}
                    label={
                      isPromptPay
                        ? t('operatorBank.copyPromptpayId')
                        : t('operatorBank.copyAccountNumber')
                    }
                  />
                </div>
                <div className="text-[12px] text-[var(--text-muted)] uppercase tracking-wide truncate">
                  {b.accountName}
                </div>
                {/* An ASK, not a rule, and worded as one. What the server
                    enforces is one floor per agent — the LOWEST across the
                    accounts that could take the transfer — so an account
                    configured above it will still accept less. Calling this a
                    second "minimum" would put two numbers on one screen with
                    nothing to say which binds. The line is dropped entirely when
                    it merely repeats the floor the form already states. */}
                {b.minDeposit !== null &&
                  (enforcedFloorMinor === null || BigInt(b.minDeposit) !== enforcedFloorMinor) && (
                    <div className="text-[11px] text-[var(--text-secondary)] bf-tnum">
                      {t('operatorBank.accountAsk', {
                        amount: accountMoney.format(BigInt(b.minDeposit)),
                      })}
                    </div>
                  )}
                {accountMoney.rate !== null && (
                  <div className="text-[11px] text-[var(--text-secondary)] bf-tnum">
                    {rateLabel(accountMoney.rate, accountMoney.currency, ledgerCurrency)}
                  </div>
                )}
              </div>
              {/* BOTH, not either, and neither of them is the account type. An
                  ordinary bank account shows a QR here whenever the operator
                  switched it on and gave it something to draw — that is what
                  the design asks for, and the bank's own code is a perfectly
                  payable thing to put beside its account number. The switch
                  alone draws nothing: an account it is on for with no payload
                  yet simply has no QR, and the rest of the card is the whole
                  instruction on its own. */}
              {showQr && b.qrPayload && (
                <div className="flex flex-col items-center gap-1.5 shrink-0">
                  <div className="p-2.5 rounded-xl bg-white">
                    <QRCodeSVG
                      value={b.qrPayload}
                      size={132}
                      title={t('operatorBank.scanQr')}
                    />
                  </div>
                  <span className="text-[11px] text-[var(--text-muted)]">
                    {t('operatorBank.scanQr')}
                  </span>
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

/**
 * The player's OWN bank accounts — the ones a deposit is filed as transferred
 * FROM. Shared between the gateway form (a TanStack Form field) and the slip
 * form (plain state) so the two never drift into rendering the account list
 * differently.
 */
function BankAccountList({
  banks,
  bankByCode,
  selectedId,
  onSelect,
  language,
  defaultBadgeLabel,
}: {
  banks: BankAccount[];
  bankByCode: Map<string, BankMaster>;
  selectedId: string;
  onSelect: (id: string) => void;
  language: string;
  defaultBadgeLabel: string;
}) {
  return (
    <div className="flex flex-col gap-3">
      {banks.map((b) => {
        const selected = b.id === selectedId;
        return (
          <button
            type="button"
            key={b.id}
            onClick={() => onSelect(b.id)}
            aria-pressed={selected}
            className={[
              'flex items-center gap-4 p-4 rounded-xl border text-left transition-all',
              selected
                ? 'border-[var(--primary)] bg-[var(--primary)]/5 shadow-[0_0_0_3px_var(--primary-glow)]'
                : 'border-[var(--border)] hover:border-[var(--border-strong)]',
            ].join(' ')}
          >
            <BankLogo code={b.bankCode} size={44} />
            <div className="flex-1 min-w-0 flex flex-col gap-0.5">
              <div className="flex items-center gap-2">
                <span className="text-[13px] font-semibold text-[var(--text-primary)] truncate">
                  {bankLabel(bankByCode.get(b.bankCode) ?? { name: b.bankCode, country: null }, language)}
                </span>
                {b.isDefault && <Badge tone="muted">{defaultBadgeLabel}</Badge>}
              </div>
              <div className="font-mono text-[17px] font-bold tracking-wide text-[var(--text-primary)] bf-tnum truncate">
                {b.accountNumber}
              </div>
              <div className="text-[12px] text-[var(--text-muted)] uppercase tracking-wide truncate">
                {b.accountName}
              </div>
            </div>
            {selected && <Icon name="check" size={20} className="shrink-0 text-[var(--primary)]" />}
          </button>
        );
      })}
    </div>
  );
}

/** One sentence and nothing else — the shape every dead end on this screen takes. */
function NoticeCard({ title, body }: { title: string; body: string }) {
  return (
    <Card className="p-8 flex flex-col items-center gap-2 text-center">
      <Icon name="triangle" size={28} className="text-[var(--warning)]" />
      <h3 className="text-[15px] font-bold">{title}</h3>
      <p className="text-[13px] text-[var(--text-muted)]">{body}</p>
    </Card>
  );
}

/**
 * What the player was told to transfer.
 *
 * Two things produce one: filing a request through the gateway form, and
 * reopening a request that is still open from the recent list. The second
 * exists because the first is lost on a reload — the account number and the
 * exact figure lived only in this component's state, so a player who closed
 * the tab mid-transfer had no way back to the instruction they were following.
 */
export interface GatewayTicket {
  id: string;
  createdAt: string;
  /** Minor units of `currency` (absent = the ledger). Null when no figure could be established. */
  amountMinor: bigint | null;
  currency?: string;
  /**
   * `finance.deposits.channel` — the rail the request was filed under.
   *
   * The deposit MODE is not stored anywhere: svc-finance folds all four of
   * them into `promptpay` or `bank_transfer` at insert. So this is the whole
   * of what a reopened request can say about how it was meant to be paid, and
   * the accounts underneath it are the same set every mode transfers into.
   */
  channel: string | null;
}

/**
 * A row from the recent list, read as a ticket.
 *
 * `amount` is a JSON number off the wire and `BigInt()` throws on anything
 * that is not an integer, so a malformed figure becomes "no figure" — the
 * panel then names the account without quoting an amount, which is the same
 * state a request filed with an unparseable amount already renders.
 */
export function depositToTicket(
  d: Pick<Deposit, 'id' | 'createdAt' | 'amount' | 'channel'>,
): GatewayTicket {
  return {
    id: d.id,
    createdAt: d.createdAt,
    amountMinor: Number.isInteger(d.amount) ? BigInt(d.amount) : null,
    channel: d.channel ?? null,
  };
}

// Mirrors svc-finance's own ceiling (`SLIP_MAX_FILE_SIZE_BYTES`, 10 MB) and
// accepted set. A client-side check here is a courtesy, not the security
// boundary — the server sniffs the real bytes rather than trusting either of
// these, so a mismatch just means a slower refusal, never a bypass.
const SLIP_MAX_FILE_BYTES = 10 * 1024 * 1024;
const SLIP_ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];

/**
 * Attach a slip, enter the amount, file the deposit — the third deposit flow,
 * servable once private slip storage existed to put the attachment in.
 *
 * Deliberately its own small form rather than a third mode on the gateway
 * form's shared `useForm` instance: a slip deposit needs an extra field
 * (the file) and a two-step submit (upload, then create), and keeping it
 * separate means a failed upload can never leave the gateway ticket state in
 * an inconsistent place.
 */
function SlipDepositForm({
  memberId,
  view,
  ledgerFloorMinor,
  sourceBanks,
  sourceBankByCode,
  bankAccountsPending,
  bankByCode,
  quickAmounts,
  minAmountMinor,
  minAmountMessage,
  minimumsPending,
  depositSchema,
  houseBanks,
  houseBanksEmptyText,
  houseBanksPending,
  houseBanksError,
  onStorageUnavailable,
}: {
  memberId: string;
  /** What the amount is typed in; `quickAmounts` and `minAmountMinor` are already in its units. */
  view: DepositInputView;
  /** The ledger floor svc-finance enforces, for comparing against account floors. */
  ledgerFloorMinor: bigint | null;
  sourceBanks: BankAccount[];
  /** Names `sourceBanks`, which are listed under the storefront's bank currency. */
  sourceBankByCode: Map<string, BankMaster>;
  bankAccountsPending: boolean;
  /** Names the house accounts, which may be in either currency. */
  bankByCode: Map<string, BankMaster>;
  quickAmounts: bigint[];
  minAmountMinor: bigint | null;
  minAmountMessage: string;
  minimumsPending: boolean;
  depositSchema: ReturnType<typeof buildDepositSchema>;
  houseBanks: HouseBank[];
  /** See `TransferTargets.emptyText` — the caller owns the reason the list is empty. */
  houseBanksEmptyText?: string;
  houseBanksPending: boolean;
  houseBanksError: boolean;
  /** The panel above this one takes over once storage answers FIN-313 for real. */
  onStorageUnavailable: () => void;
}) {
  const { t, i18n } = useTranslation('deposit');
  const formatDisplay = useFormatMoney();
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const currency = view.currency;

  const [bankAccountId, setBankAccountId] = useState(sourceBanks[0]?.id ?? '');
  const [amount, setAmount] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const slipMutation = useMutation({
    mutationFn: async () => {
      // Same shape as the gateway mutation's own guard: refuse before any
      // request goes out rather than post a value nothing typed. Checked
      // before the upload, not after, so a re-parse failure costs no wasted
      // file transfer.
      const parsed = tryToMinorUnits(normalizeAmountInput(amount), currency);
      const ledgerMinor = parsed.ok ? view.toLedger(parsed.minor) : 0n;
      if (ledgerMinor <= 0n) throw new Error(t('form.invalidAmount'));
      const upload = await uploadDepositSlip(file!);
      return createDeposit({
        memberId,
        bankAccountId,
        ...depositAmountFields(view, parsed.ok ? parsed.minor : 0n),
        mode: 'slip',
        slipRef: upload.slipRef,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: FINANCE_QUERY_KEYS.deposits() });
      void qc.invalidateQueries({ queryKey: WALLET_QUERY_KEYS.balances });
      toast.success(t('form.success'));
      setFile(null);
      setAmount('');
      setFormError(null);
    },
    onError: (err) => {
      // FIN-313 is "temporarily off", not a validation mistake — the panel
      // above swaps to the reason instead of a toast the player can dismiss
      // and then retry against a control that can only fail again.
      if (err instanceof ApiError && err.code === 'FIN-313') {
        onStorageUnavailable();
        return;
      }
      toast.error(mapApiError(err, t, formatDisplay));
    },
  });

  function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
    const picked = e.target.files?.[0];
    e.target.value = '';
    if (!picked) return;
    if (picked.size > SLIP_MAX_FILE_BYTES) {
      toast.error(t('modes.slip.fileTooLarge'));
      return;
    }
    // `picked.type` is the browser's OWN guess, and several Android file
    // providers hand back '' for a perfectly valid file. The server sniffs
    // magic bytes and is the real authority (FIN-311), so an unknown type
    // costs one slower server refusal rather than a wrong local one.
    if (picked.type && !SLIP_ACCEPTED_TYPES.includes(picked.type)) {
      toast.error(t('modes.slip.fileTypeInvalid'));
      return;
    }
    setFile(picked);
    setFormError(null);
  }

  // An 11px line under a field is not where a player looks after pressing a
  // button they expected to work — #235 was reported as "nothing happens".
  // Toast AND keep the inline line: the toast says something went wrong, the
  // line says which field.
  function refuse(message: string) {
    setFormError(message);
    toast.error(message);
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const parsedAmount = depositSchema.shape.amount.safeParse(amount);
    if (!parsedAmount.success) {
      refuse(parsedAmount.error.errors[0]?.message ?? t('form.validationError'));
      return;
    }
    const parsedBank = depositSchema.shape.bankAccountId.safeParse(bankAccountId);
    if (!parsedBank.success) {
      refuse(t('form.bankRequired'));
      return;
    }
    if (!file) {
      refuse(t('modes.slip.fileRequired'));
      return;
    }
    setFormError(null);
    slipMutation.mutate();
  }

  const typedMinor = tryToMinorUnits(normalizeAmountInput(amount || '0'), currency);
  const submitAmountLabel = typedMinor.ok ? formatMoney(typedMinor.minor, currency) : amount;
  const submitLabel = amount ? t('form.submit', { amount: submitAmountLabel }) : t('form.submitEmpty');

  return (
    <>
      <TransferTargets
        banks={houseBanks}
        emptyText={houseBanksEmptyText}
        bankByCode={bankByCode}
        ledgerCurrency={view.ledgerCurrency}
        // Unlike the automatic flow's card, this one DOES post to
        // POST /deposits and DOES state the enforced floor further down —
        // passing the real value here is what lets TransferTargets suppress
        // an account's own figure when it merely repeats that floor.
        enforcedFloorMinor={ledgerFloorMinor}
        isPending={houseBanksPending}
        isError={houseBanksError}
      />
      <Card className="p-5 md:p-6 flex flex-col gap-5">
        <div className="flex items-baseline justify-between">
          <h3 className="text-[15px] font-bold">{t('form.heading')}</h3>
          <span className="font-mono text-[11px] text-[var(--text-muted)]">{currency}</span>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <label className="text-[11px] font-bold uppercase tracking-wide text-[var(--text-secondary)]">
              {t('form.fromBank')}
            </label>
            {bankAccountsPending ? (
              <div className="flex flex-col gap-3">
                {[1, 2].map((i) => (
                  <div key={i} className="h-[88px] rounded-xl bg-[var(--bg-elevated)]/50 animate-pulse" />
                ))}
              </div>
            ) : sourceBanks.length === 0 ? (
              <div className="text-[12px] text-[var(--text-muted)] py-2">
                <Link
                  to="/profile"
                  className="text-[var(--primary)] underline underline-offset-2 font-semibold"
                >
                  {t('form.noBanksCta')}
                </Link>
              </div>
            ) : (
              <BankAccountList
                banks={sourceBanks}
                bankByCode={sourceBankByCode}
                selectedId={bankAccountId}
                onSelect={setBankAccountId}
                language={i18n.language}
                defaultBadgeLabel={t('form.defaultBadge')}
              />
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-[11px] font-bold uppercase tracking-wide text-[var(--text-secondary)]">
              {t('form.amount')}
            </label>
            <Input
              prefix={currencyGlyph(currency)}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputMode="decimal"
              placeholder={minAmountMinor === null ? undefined : minAmountMessage}
            />
            <CreditEstimate view={view} typed={amount} />
            <div className="flex items-center justify-between text-[11px] text-[var(--text-muted)]">
              <span>{t('rules.minimum')}</span>
              <span className="font-semibold text-[var(--text-secondary)] bf-tnum">
                {minimumsPending
                  ? '…'
                  : minAmountMinor === null
                    ? t('rules.noMinimum')
                    : formatMoney(minAmountMinor, currency)}
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-1">
              {quickAmounts.map((minor) => {
                const belowFloor = minAmountMinor !== null && minor < minAmountMinor;
                return (
                  <button
                    type="button"
                    key={String(minor)}
                    onClick={() => setAmount(minorToPlain(minor, currency))}
                    disabled={belowFloor}
                    className="h-10 px-3.5 rounded-lg bg-[var(--bg-elevated)] border border-[var(--border)] hover:border-[var(--primary)] text-[13px] font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-[var(--border)]"
                  >
                    {formatMoney(minor, currency)}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-[11px] font-bold uppercase tracking-wide text-[var(--text-secondary)]">
              {t('modes.slip.attachLabel')}
            </label>
            {file ? (
              <div className="flex items-center justify-between gap-2 p-3 rounded-xl border border-[var(--border)]">
                <div className="flex items-center gap-2 min-w-0">
                  <Paperclip size={16} className="shrink-0 text-[var(--text-secondary)]" aria-hidden />
                  <span className="text-[12px] text-[var(--text-primary)] truncate">{file.name}</span>
                </div>
                <button
                  type="button"
                  onClick={() => setFile(null)}
                  aria-label={t('modes.slip.removeFile')}
                  className="shrink-0 h-7 w-7 grid place-items-center rounded-lg text-[var(--text-secondary)] hover:text-[var(--danger)] transition-colors"
                >
                  <X size={14} aria-hidden />
                </button>
              </div>
            ) : (
              <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()}>
                {t('modes.slip.attachButton')}
              </Button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept={SLIP_ACCEPTED_TYPES.join(',')}
              tabIndex={-1}
              className="sr-only"
              onChange={handleFileChange}
              aria-label={t('modes.slip.attachLabel')}
            />
            <p className="text-[11px] text-[var(--text-muted)]">{t('modes.slip.fileHint')}</p>
          </div>

          {formError && <span className="text-[12px] font-semibold text-[var(--danger)]">{formError}</span>}

          <Button
            type="submit"
            size="lg"
            glow
            fullWidth
            disabled={slipMutation.isPending || sourceBanks.length === 0}
          >
            {slipMutation.isPending ? t('form.submitting') : submitLabel}
          </Button>
          <div className="text-[11px] text-[var(--text-muted)] leading-relaxed">
            {t('modes.slip.hint')}
          </div>
        </form>
      </Card>
    </>
  );
}

function DepositScreen() {
  const { t, i18n } = useTranslation('deposit');
  const qc = useQueryClient();
  const subject = useAuthStore((s) => s.subject);
  const memberId = subject?.id ?? '';

  const ledgerCurrency = useMoneyCurrency();
  const siteMoney = useDisplayMoney();
  const displayCurrency = siteMoney.currency;
  const formatDisplay = useFormatMoney();
  const configQuery = useFinanceConfig();
  const config = configQuery.data ?? FINANCE_CONFIG_DEFAULTS;
  const quickAmounts = useMemo(() => config.depositQuickAmounts.map(BigInt), [config.depositQuickAmounts]);
  // The agent-level floor. An unresolved read leaves it null and lets the player
  // through to the server's own FIN-306 — a hint that cannot be fetched must not
  // become a refusal the operator never configured. The minimum line still
  // distinguishes the two: "not back yet" renders as a placeholder, and only a
  // RESOLVED null says there is no minimum.
  const minimumsQuery = useTransferMinimums();
  const minAmountMinor =
    minimumsQuery.data?.minDeposit == null ? null : BigInt(minimumsQuery.data.minDeposit);
  const bankMastersQuery = useBankMasters({ currency: displayCurrency });
  const bankByCode = useMemo(
    () => new Map((bankMastersQuery.data?.items ?? []).map((m) => [m.code, m])),
    [bankMastersQuery.data],
  );
  const sourceBankMastersQuery = useBankMasters({ currency: useBankCurrency() });
  const sourceBankByCode = useMemo(
    () => new Map((sourceBankMastersQuery.data?.items ?? []).map((m) => [m.code, m])),
    [sourceBankMastersQuery.data],
  );

  const houseBanksQuery = useHouseBanks();
  // Deposit-capable rails only. `usage` is the operator's own three-value
  // column: a withdraw-only account is a payout source, not somewhere a player
  // should be sending money.
  const houseBanks = useMemo(
    () =>
      (houseBanksQuery.data?.banks ?? []).filter(
        (b) => b.usage === 'deposit' || b.usage === 'both',
      ),
    [houseBanksQuery.data],
  );

  const bankAccountsQuery = useQuery({
    queryKey: MEMBER_QUERY_KEYS.bankAccounts(memberId),
    queryFn: () => fetchBankAccounts(memberId),
    enabled: !!memberId,
    staleTime: 30_000,
  });

  const depositsQuery = useQuery({
    queryKey: FINANCE_QUERY_KEYS.deposits(),
    queryFn: () => fetchDeposits({ pageSize: 20 }),
    staleTime: 30_000,
  });

  /**
   * The member's own claim rows, serving BOTH promo surfaces on this screen: the
   * tag naming what the next deposit will spend, and the bonus beside each
   * settled deposit in the recent list.
   *
   * Deliberately the same key `/promos` already holds, so a player arriving
   * from there has both answers before the first paint and the two screens
   * never issue the request twice. `refetchOnMount: 'always'` is the part that
   * matters here and not there: a stale "no promotion" tag is exactly the
   * mistake this screen exists to prevent, and a claim made seconds ago on
   * another screen must be on this one.
   *
   * 200 is the server's own hard cap, and `/promos` asks for the same number for
   * the same key — two limits behind one key would mean whichever screen mounted
   * first silently decided the other's window. The window has to outrun the
   * deposit list it is joined against: a member with hundreds of check-in and
   * rebate claims would otherwise push the claim that names an older deposit's
   * bonus out of the response, and that row would lose its bonus line.
   */
  const claimsQuery = useQuery({
    queryKey: PROMO_QUERY_KEYS.myClaims({ status: 'recent' }),
    queryFn: () => fetchMyClaims({ limit: 200 }),
    staleTime: 60_000,
    refetchOnMount: 'always',
  });
  const claims = claimsQuery.data?.items;
  // The clock is read when the answer changes, not on a timer. An expiry that
  // passes with the page open leaves the tag naming a reservation the server has
  // stopped honouring, until the next refetch — which is the same window every
  // other fact on this screen sits in, and far better than a tag that ticks.
  const reservation = useMemo(() => findDepositReservation(claims, Date.now()), [claims]);
  const bonusByDeposit = useMemo(() => indexBonusByDeposit(claims), [claims]);

  const enabledModes = useMemo(() => enabledDepositModes(config), [config]);
  // The picker is a preference, not a source of truth: an operator switching a
  // flow off while the page is open must not leave the screen on a tab that no
  // longer exists, so the stored choice is validated against the live set on
  // every render rather than corrected by an effect.
  const [pickedMode, setPickedMode] = useState<DepositMode | null>(null);
  const activeMode =
    pickedMode && enabledModes.includes(pickedMode) ? pickedMode : (enabledModes[0] ?? null);

  const [ticket, setTicket] = useState<GatewayTicket | null>(null);
  // Set once svc-finance has actually answered a slip attempt with 503
  // FIN-313 — never assumed up front. Storage exists in the platform now, so
  // treating "not yet tried" as "unavailable" would reintroduce the old
  // always-off behaviour under a new name.
  const [slipStorageUnavailable, setSlipStorageUnavailable] = useState(false);

  /**
   * The amount box of one flow, in the currency that flow's accounts receive
   * (or the site shows): the floor and the shortcuts converted into it, and the
   * schema checking the typed figure against them.
   */
  function amountFormFor(mode: DepositMode) {
    const view = depositInputView(banksForMode(houseBanks, mode), siteMoney);
    const min = minAmountMinor === null ? null : view.toTarget(minAmountMinor);
    const messages = depositMessages(t, view.currency, min);
    return {
      view,
      min,
      quick: quickAmounts.map(view.toTarget),
      messages,
      schema: buildDepositSchema(view.currency, min, messages),
    };
  }
  const gateway = amountFormFor('gateway');
  const currency = gateway.view.currency;
  const depositSchema = gateway.schema;
  const minAmountMessage = gateway.messages.minAmount;

  const allBanks = bankAccountsQuery.data?.items ?? [];
  const sourceBanks = depositSourceBanks(allBanks);

  const form = useForm<DepositForm>({
    defaultValues: {
      ...DEPOSIT_FORM_DEFAULTS,
      bankAccountId: sourceBanks[0]?.id ?? DEPOSIT_FORM_DEFAULTS.bankAccountId,
    },
    onSubmit: async ({ value }) => {
      const parsed = depositSchema.safeParse(value);
      if (!parsed.success) {
        toast.error(parsed.error.errors[0]?.message ?? t('form.validationError'));
        return;
      }
      await depositMutation.mutateAsync(parsed.data);
    },
  });

  const depositMutation = useMutation({
    mutationFn: (values: DepositForm) => {
      // `* 100` assumed every currency has two decimals: a JPY player typing
      // 1000 sent 100000 minor units — ¥100,000, a 100× over-deposit.
      const parsed = tryToMinorUnits(normalizeAmountInput(values.amount), currency);
      const ledgerMinor = parsed.ok ? gateway.view.toLedger(parsed.minor) : 0n;
      if (ledgerMinor <= 0n) throw new Error(t('form.invalidAmount'));
      return createDeposit({
        memberId,
        bankAccountId: values.bankAccountId,
        ...depositAmountFields(gateway.view, parsed.ok ? parsed.minor : 0n),
        mode: 'gateway',
      });
    },
    onSuccess: (row, values) => {
      void qc.invalidateQueries({ queryKey: FINANCE_QUERY_KEYS.deposits() });
      void qc.invalidateQueries({ queryKey: WALLET_QUERY_KEYS.balances });
      toast.success(t('form.success'));
      // Re-parsed from the text the player typed rather than read back off the
      // wire: the row's `amount` is a JSON number, and BigInt() throws on
      // anything that is not an integer.
      const parsed = tryToMinorUnits(normalizeAmountInput(values.amount), currency);
      // The instruction names what the account receives: the typed figure for an
      // account that prices it, otherwise the ledger figure the site rate came to.
      const priced = gateway.view.transferAccountId !== null;
      setTicket({
        id: row.id,
        createdAt: row.createdAt,
        amountMinor: !parsed.ok ? null : priced ? parsed.minor : gateway.view.toLedger(parsed.minor),
        currency: priced ? currency : ledgerCurrency,
        channel: row.channel ?? null,
      });
      form.reset();
    },
    onError: (err) => {
      toast.error(mapApiError(err, t, formatDisplay));
    },
  });

  function handleQuickAmount(minor: bigint) {
    form.setFieldValue('amount', minorToPlain(minor, currency));
    // The withdraw twin's silent submit (#138): `setFieldValue` skips the
    // field's validators, so an error from an earlier submit survives the
    // amount that fixed it, holds `canSubmit` at false, and the next submit
    // returns without a request or a toast.
    void form.validateField('amount', 'change');
  }

  /**
   * Reopen the transfer instruction for a request that is still waiting on the
   * player's money — the same panel filing one produces, rebuilt from the row.
   *
   * The scroll is not decoration: the control that opens this sits in the
   * recent list, below everything the panel replaces, so on a phone the player
   * would press it and watch nothing appear.
   */
  function resumeDeposit(d: Deposit) {
    setTicket(depositToTicket(d));
    if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  const watchAmount = form.useStore((s) => s.values.amount);
  // The label re-renders on every keystroke against the RAW text the player is
  // typing, so it must never be able to throw — a comma is one character away
  // and there is no route-level boundary to catch it (WEB-22).
  const typedMinor = tryToMinorUnits(watchAmount || '0', currency);
  const submitAmountLabel = typedMinor.ok
    ? formatMoney(typedMinor.minor, currency)
    : (watchAmount ?? '');
  // An empty field quotes no figure at all, or the button reads "Confirm
  // deposit · THB 0.00" before the player has typed anything.
  const submitLabel = watchAmount
    ? t('form.submit', { amount: submitAmountLabel })
    : t('form.submitEmpty');

  // Only the automatic flow's answer depends on this fetch, and only once it has
  // actually resolved: refusing while it is in flight would turn a slow network
  // into "deposits are unavailable", and the pending state belongs to the card
  // that renders the accounts.
  const houseBanksResolved = !houseBanksQuery.isPending && !houseBanksQuery.isError;

  /**
   * The accounts to point a player at on ONE flow, and what to say when there
   * are none.
   *
   * The operator puts each account on a single flow (or on all of them), so a
   * tab must never print an account the operator does not reconcile against for
   * that tab — a transfer into it is money arriving where nobody is looking for
   * it. An agent whose accounts are simply all on other flows is a different
   * empty state from one with no house account at all, and says so.
   */
  function targetsFor(mode: DepositMode): { banks: HouseBank[]; emptyText?: string } {
    const banks = banksForMode(houseBanks, mode);
    return banks.length === 0 && houseBanks.length > 0
      ? { banks, emptyText: t('operatorBank.emptyForMode') }
      : { banks };
  }

  function blockerFor(mode: DepositMode): DepositModeBlocker | null {
    if (mode === 'auto' && !houseBanksResolved) return null;
    return depositModeBlocker(mode, {
      // The automatic flow IS the account, so what decides whether it can be
      // served is the count on THAT flow — not the agent's whole list, which
      // would promise a tab the filter has just emptied.
      houseAccountCount: banksForMode(houseBanks, mode).length,
      slipStorageUnavailable,
    });
  }

  const depositForm = (
    <Card className="p-5 md:p-6 flex flex-col gap-5">
      <div className="flex items-baseline justify-between">
        <h3 className="text-[15px] font-bold">{t('form.heading')}</h3>
        <span className="font-mono text-[11px] text-[var(--text-muted)]">{currency}</span>
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); form.handleSubmit(); }}
        className="flex flex-col gap-6"
      >
        {/* Bank selector — the player's own accounts, shown prominently above the amount */}
        <form.Field name="bankAccountId" validators={{ onChange: ({ value }) => {
          const r = depositSchema.shape.bankAccountId.safeParse(value);
          return r.success ? undefined : t('form.bankRequired');
        }}}>
          {(field) => (
            <div className="flex flex-col gap-2">
              <label className="text-[11px] font-bold uppercase tracking-wide text-[var(--text-secondary)]">
                {t('form.fromBank')}
              </label>
              {bankAccountsQuery.isPending ? (
                <div className="flex flex-col gap-3">
                  {[1, 2].map((i) => (
                    <div key={i} className="h-[88px] rounded-xl bg-[var(--bg-elevated)]/50 animate-pulse" />
                  ))}
                </div>
              ) : allBanks.length === 0 ? (
                <div className="text-[12px] text-[var(--text-muted)] py-2">
                  <Link
                    to="/profile"
                    className="text-[var(--primary)] underline underline-offset-2 font-semibold"
                  >
                    {t('form.noBanksCta')}
                  </Link>
                </div>
              ) : (
                <BankAccountList
                  banks={sourceBanks}
                  bankByCode={sourceBankByCode}
                  selectedId={field.state.value}
                  onSelect={field.handleChange}
                  language={i18n.language}
                  defaultBadgeLabel={t('form.defaultBadge')}
                />
              )}
              {field.state.meta.errors.length > 0 && (
                <span className="text-[12px] font-semibold text-[var(--danger)]">
                  {field.state.meta.errors[0]}
                </span>
              )}
            </div>
          )}
        </form.Field>

        {/* Amount field */}
        <form.Field name="amount" validators={{ onChange: ({ value }) => {
          const r = depositSchema.shape.amount.safeParse(value);
          return r.success ? undefined : r.error.errors[0]?.message;
        }}}>
          {(field) => (
            <div className="flex flex-col gap-1.5">
              <label className="text-[11px] font-bold uppercase tracking-wide text-[var(--text-secondary)]">
                {t('form.amount')}
              </label>
              <Input
                prefix={currencyGlyph(currency)}
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                inputMode="decimal"
                placeholder={gateway.min === null ? undefined : minAmountMessage}
              />
              {field.state.meta.errors.length > 0 && (
                <span className="text-[12px] font-semibold text-[var(--danger)]">
                  {field.state.meta.errors[0]}
                </span>
              )}
              <CreditEstimate view={gateway.view} typed={field.state.value} />
              <div className="flex items-center justify-between text-[11px] text-[var(--text-muted)]">
                <span>{t('rules.minimum')}</span>
                <span className="font-semibold text-[var(--text-secondary)] bf-tnum">
                  {minimumsQuery.isPending
                    ? '…'
                    : gateway.min === null
                      ? t('rules.noMinimum')
                      : formatMoney(gateway.min, currency)}
                </span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-1">
                {gateway.quick.map((minor) => {
                  // A shortcut under the floor is a dead control: it fills in
                  // an amount this same screen then refuses.
                  const belowFloor = gateway.min !== null && minor < gateway.min;
                  return (
                    <button
                      type="button"
                      key={String(minor)}
                      onClick={() => handleQuickAmount(minor)}
                      disabled={belowFloor}
                      className="h-10 px-3.5 rounded-lg bg-[var(--bg-elevated)] border border-[var(--border)] hover:border-[var(--primary)] text-[13px] font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-[var(--border)]"
                    >
                      {formatMoney(minor, currency)}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </form.Field>

        <Button
          type="submit"
          size="lg"
          glow
          fullWidth
          disabled={depositMutation.isPending || sourceBanks.length === 0}
        >
          {depositMutation.isPending ? t('form.submitting') : submitLabel}
        </Button>
        <div className="text-[11px] text-[var(--text-muted)] leading-relaxed">
          {t('form.hint')}
        </div>
      </form>
    </Card>
  );

  /**
   * The instruction the gateway flow exists to produce: an account, and the
   * exact figure to send it.
   *
   * There is no countdown here and there must not be one. `finance.deposits`
   * carries no expiry, nothing sweeps a stale request, and refusing a late
   * approval would mean a transfer the player really made could no longer be
   * credited. A timer nothing enforces is worse than no timer, so the panel
   * states what is true: the request stays open until an operator has checked it.
   */
  const gatewayInstruction = ticket && (
    <>
      <TransferTargets
        {...targetsFor('gateway')}
        bankByCode={bankByCode}
        ledgerCurrency={ledgerCurrency}
        enforcedFloorMinor={minAmountMinor}
        isPending={houseBanksQuery.isPending}
        isError={houseBanksQuery.isError}
        title={
          ticket.amountMinor === null
            ? t('transferTo.title')
            : t('gateway.instructionTitle', {
                amount: ticket.currency
                  ? formatMoney(ticket.amountMinor, ticket.currency)
                  : gateway.view.transferAccountId
                    ? gateway.view.format(ticket.amountMinor)
                    : formatMoney(ticket.amountMinor, ledgerCurrency),
              })
        }
        hint={
          <p className="text-[12px] text-[var(--text-muted)] leading-relaxed">
            {t('gateway.instructionBody')}
          </p>
        }
      />
      <Card className="p-5 md:p-6 flex flex-col gap-3">
        <div className="flex flex-col gap-1 text-[12px] text-[var(--text-secondary)]">
          {/* The rail, echoed back from the row rather than from whichever tab
              happens to be selected now — a request reopened days later must
              describe how IT was filed, not what the screen is showing. */}
          {ticket.channel && (
            <span>
              {t('gateway.method', {
                method: t(`recent.method.${ticket.channel}`, {
                  defaultValue: t('recent.method.other'),
                }),
              })}
            </span>
          )}
          <span className="bf-tnum">{t('gateway.reference', { ref: ticket.id.slice(0, 8) })}</span>
          <span className="bf-tnum">
            {t('gateway.submittedAt', { when: formatDateTime(ticket.createdAt, i18n.language) })}
          </span>
        </div>
        <Button type="button" variant="outline" onClick={() => setTicket(null)}>
          {t('gateway.newDeposit')}
        </Button>
      </Card>
    </>
  );

  /**
   * What a row in the recent list can say about promotions — two different
   * answers, because a deposit's own bonus does not exist until it settles.
   *
   * `finance.deposits` knows nothing about promotions, so the link is followed
   * from the other end: the claim row carries `deposit_id`, stamped when the
   * deposit-approved consumer upgraded the reservation. A settled deposit
   * therefore has a claim to join and states the bonus it produced. A deposit
   * still waiting on an operator has none, and states the reservation queued
   * behind it by name — no figure.
   */
  function bonusNoteFor(deposit: Deposit): ReactNode {
    const claim = bonusByDeposit.get(deposit.id);
    if (claim) {
      const amount = formatDisplay(bigIntish(claim.grantedAmount));
      return (
        <span className="inline-flex items-center gap-1 font-semibold text-[var(--success)]">
          <Icon name="gift" size={12} className="shrink-0" />
          <span className="truncate">
            {claim.promotionTitle
              ? t('recent.bonusWithPromo', { amount, promo: claim.promotionTitle })
              : t('recent.bonus', { amount })}
          </span>
        </span>
      );
    }
    // The row in the screenshot: a request an operator has not answered yet, and
    // the one the player most wants confirmed. It names the reservation and no
    // figure — the grant is computed from the deposit amount at approval, so any
    // number here would be this screen's guess at svc-promo's arithmetic.
    if (!reservation || depositStatusToDisplay(deposit.status) !== 'pending') return undefined;
    return (
      <span className="inline-flex items-center gap-1 text-[var(--text-muted)]">
        <Icon name="gift" size={12} className="shrink-0" />
        <span className="truncate">
          {t('recent.reservedPromo', {
            promo: reservation.promotionTitle ?? t('promoTag.unnamed'),
          })}
        </span>
      </span>
    );
  }

  /**
   * Which promotion this deposit is about to spend — or that it is about to
   * spend none.
   *
   * Above the method area rather than inside the amount form, because all three
   * flows and the reopened transfer instruction carry the same risk: a player
   * who believes a bonus is attached transfers money that arrives without one,
   * and the reservation cannot be applied afterwards (#394).
   *
   * A label of null is the screen having nothing to state yet — the claim query
   * is in flight, or it failed. Both draw nothing: "no promotion" asserted from
   * an unanswered query is the very error this tag exists to prevent.
   *
   * It resolves independently of the operator config, so it can appear above the
   * skeleton the method area shows while that is still in flight. Cosmetic: the
   * two queries answer a few hundred ms apart and the tag states nothing the
   * config could contradict.
   */
  const promoTagLabel =
    claimsQuery.isPending || claimsQuery.isError
      ? null
      : reservation
        ? t('promoTag.reserved', { promo: reservation.promotionTitle ?? t('promoTag.unnamed') })
        : t('promoTag.none');
  const promoTag = promoTagLabel !== null && (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-[var(--border)] bg-[var(--bg-raised)] px-4 py-3">
      <span className="flex items-center gap-2 min-w-0">
        <Icon name="gift" size={16} className="shrink-0 text-[var(--primary)]" />
        <span className="text-[12px] font-semibold text-[var(--text-primary)] truncate">
          {promoTagLabel}
        </span>
      </span>
      {!reservation && (
        <Link
          to="/promos"
          className="shrink-0 text-[12px] font-semibold text-[var(--primary)] underline underline-offset-2"
        >
          {t('promoTag.pickCta')}
        </Link>
      )}
    </div>
  );

  function panelFor(mode: DepositMode) {
    const blocker = blockerFor(mode);
    if (blocker) {
      return <NoticeCard title={t('modes.blocked.title')} body={t(`modes.blocked.${blocker}`)} />;
    }
    if (mode === 'auto') {
      return (
        <TransferTargets
          {...targetsFor('auto')}
          bankByCode={bankByCode}
          ledgerCurrency={ledgerCurrency}
          // Deliberately null, not the agent floor. That floor is only ever
          // enforced on POST /deposits, and this flow posts nothing — so
          // suppressing an account's own figure for matching it would leave the
          // player with no figure at all.
          enforcedFloorMinor={null}
          isPending={houseBanksQuery.isPending}
          isError={houseBanksQuery.isError}
        />
      );
    }
    if (mode === 'slip') {
      const slip = amountFormFor('slip');
      return (
        <SlipDepositForm
          memberId={memberId}
          view={slip.view}
          ledgerFloorMinor={minAmountMinor}
          sourceBanks={sourceBanks}
          sourceBankByCode={sourceBankByCode}
          bankAccountsPending={bankAccountsQuery.isPending}
          bankByCode={bankByCode}
          quickAmounts={slip.quick}
          minAmountMinor={slip.min}
          minAmountMessage={slip.messages.minAmount}
          minimumsPending={minimumsQuery.isPending}
          depositSchema={slip.schema}
          houseBanks={targetsFor('slip').banks}
          houseBanksEmptyText={targetsFor('slip').emptyText}
          houseBanksPending={houseBanksQuery.isPending}
          houseBanksError={houseBanksQuery.isError}
          onStorageUnavailable={() => setSlipStorageUnavailable(true)}
        />
      );
    }
    // Only the gateway flow reaches here: `auto` and `slip` both returned above.
    // An open ticket never gets this far — it replaces the whole mode area,
    // tabs included, before `panelFor` is called at all.
    return depositForm;
  }

  // The config query answers from a placeholder first, and the placeholder is a
  // GUESS at which flows are on. Rendering the guess means the panel can be
  // swapped out from under a player who has started typing an amount, so the
  // mode area waits. An error is not a reason to wait forever: the query keeps
  // returning the placeholder after a failed fetch, and a screen that never
  // resolves is a worse answer than the platform default.
  const configPending = configQuery.isPlaceholderData && !configQuery.isError;

  // The subtitle describes the FLOW the player is looking at, so it has nothing
  // to say while that is still unknown, when deposits are stopped, when no flow
  // is on — or when the flow on screen is one this brand cannot serve, where it
  // would promise the very thing the panel underneath refuses.
  const subtitle =
    !configPending && config.depositEnabled && activeMode && !blockerFor(activeMode)
      ? t(`modes.${activeMode}.subtitle`)
      : t('subtitle');

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title={t('title')} subtitle={subtitle} />

      {promoTag}

      {configPending ? (
        <div className="h-[220px] rounded-xl bg-[var(--bg-elevated)]/50 animate-pulse" />
      ) : ticket ? (
        /* An open request outranks everything below it, the operator kill
           switch included. The switch stops NEW deposits and this panel files
           none: it repeats an instruction the player may already be acting
           on, and the account it names is the one thing they cannot look up
           anywhere else. It also outranks the tabs, because a member with a
           request in flight is refused a second one (FIN-005) — leaving the
           form beside the instruction would offer a control the server
           refuses. `gateway.newDeposit` clears the ticket and brings the mode
           area back. */
        gatewayInstruction
      ) : !config.depositEnabled ? (
        <NoticeCard title={t('unavailable.title')} body={t('unavailable.body')} />
      ) : activeMode === null ? (
        <NoticeCard title={t('modes.none.title')} body={t('modes.none.body')} />
      ) : enabledModes.length === 1 ? (
        panelFor(activeMode)
      ) : (
        <Tabs
          value={activeMode}
          onValueChange={(v) => setPickedMode(v as DepositMode)}
          className="flex flex-col gap-6"
        >
          <TabsList aria-label={t('modes.pickerLabel')}>
            {enabledModes.map((mode) => (
              <TabsTrigger key={mode} value={mode}>
                {t(`modes.${mode}.label`)}
              </TabsTrigger>
            ))}
          </TabsList>
          {enabledModes.map((mode) => (
            <TabsContent key={mode} value={mode}>
              {panelFor(mode)}
            </TabsContent>
          ))}
        </Tabs>
      )}

      {/* Recent deposits */}
      <section className="flex flex-col gap-3">
        <h2 className="text-[15px] font-bold uppercase tracking-wide text-[var(--text-secondary)]">
          {t('recent.title')}
        </h2>
        {depositsQuery.isPending ? (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-14 rounded-xl bg-[var(--bg-elevated)]/50 animate-pulse" />
            ))}
          </div>
        ) : depositsQuery.data?.items.length === 0 ? (
          <div className="rounded-xl border border-[var(--border)] bg-[var(--bg-raised)] py-8 text-center text-[var(--text-muted)] text-[13px]">
            {t('recent.empty')}
          </div>
        ) : (
          <Card className="overflow-hidden">
            {(depositsQuery.data?.items ?? []).map((d, i) => {
              // Arrived in another currency: that figure is the headline, the
              // ledger credit and the rate stored with the deposit sit beneath it.
              const arrivedIn =
                d.originalCurrency && d.originalAmount != null ? d.originalCurrency : null;
              const bonusNote = bonusNoteFor(d);
              return (
              <TransactionRow
                key={d.id}
                icon={ArrowDownToLine}
                title={t(`recent.method.${d.channel}`, { defaultValue: t('recent.method.other') })}
                badge={<StatusPill status={depositStatusToDisplay(d.status)} />}
                subtitle={`${d.id.slice(0, 8)} · ${formatDateTime(d.createdAt, i18n.language)}${
                  d.rejectReason ? ` · ${d.rejectReason}` : ''
                }`}
                note={
                  arrivedIn ? (
                    <>
                      <span className="block truncate text-[var(--text-muted)] bf-tnum">
                        {t('recent.credited', { amount: formatMoney(d.amount, ledgerCurrency) })}
                        {d.convertRate ? ` · ${rateLabel(d.convertRate, arrivedIn, ledgerCurrency)}` : ''}
                      </span>
                      {bonusNote}
                    </>
                  ) : (
                    bonusNote
                  )
                }
                amountMinor={arrivedIn ? (d.originalAmount ?? 0) : d.amount}
                currency={arrivedIn ?? ledgerCurrency}
                tone="credit"
                hasBorder={i > 0}
                action={
                  isResumableDeposit(d.status) || isCancellableDeposit(d.status) ? (
                    <div className="flex items-center gap-1 shrink-0">
                      {isResumableDeposit(d.status) && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => resumeDeposit(d)}
                        >
                          {t('recent.resume')}
                        </Button>
                      )}
                      {isCancellableDeposit(d.status) && <CancelDepositButton depositId={d.id} />}
                    </div>
                  ) : undefined
                }
              />
              );
            })}
          </Card>
        )}
      </section>
    </div>
  );
}
